import { APP_SCORING_FORMAT, APP_SCORING_MAX_SCORE } from '@/constants/config';
import { FEATURE_FLAGS } from '@/constants/feature-flags';
import { formatScore } from './score-format-policy';
import { getSkillLevel } from './skill-level-policy';
import { isPassApplicable } from './pass-policy';
import { selectSkillLevelInputValue } from './score-value-selection-policy';
import { SCORE_LABELS, SKILL_LEVELS, TAG_LABELS, WORDS } from '@/constants/labels';
import {
  TNormalizedRatingCutoffs,
  ISimulationScore,
  TScoreSkillLevel,
  TSimulationType,
} from '@/types';

// ================================
// Module Overview
// ================================

/**
 * This policy module owns score-history badge decisions that were previously
 * embedded in component-level utilities.
 *
 * Why this module exists:
 * - Keeps presentation components focused on rendering, not rule authoring.
 * - Centralizes pass/fail and skill-level color semantics used by score history.
 * - Reduces risk of behavior drift when score display rules evolve.
 *
 * Scope of responsibility:
 * - Badge color resolution (e.g. pass/fail vs. skill-level color mapping).
 * - Badge text formatting policy (pass/fail label vs. formatted score).
 *
 * Explicitly out of scope:
 * - Date/time formatting (remains in UI utility helpers).
 * - List ordering/filtering and score-history entry derivation (separate policies).
 */

// ================================
// Types
// ================================

/**
 * Supported visual variants for score-history badges.
 *
 * Notes:
 * - `pass`/`fail` are used for pass-applicable simulation types
 *   (certification/practice).
 * - The remaining color tokens map to score skill-level bands.
 */
export type TScoreHistoryBadgeColor =
  | 'purple'
  | 'orange'
  | 'yellow'
  | 'green'
  | 'blue'
  | 'pass'
  | 'fail';

/**
 * Input contract for score-history badge decision helpers.
 *
 * Field semantics:
 * - `score` is required because both color and text rules depend on it when
 *   pass/fail cannot be decided via explicit `passed`.
 * - `maxScore` is optional and falls back to app-level configuration.
 * - `passed` is optional because some score payloads omit a computed pass flag.
 * - `simulationType` is optional to preserve existing permissive call-sites.
 */
export interface IScoreHistoryDisplayDecisionInput {
  /** Maximum score denominator used for threshold or format calculations. */
  maxScore?: number;

  /** Optional explicit pass/fail verdict when already computed upstream. */
  passed?: boolean;

  /** Numeric score value to evaluate/format for the badge. */
  score: number;

  /** Optional runtime L1-L4 percentage cutoffs for skill-level resolution. */
  runtimeRatingCutoffs?: TNormalizedRatingCutoffs;

  /** Simulation type that determines whether pass/fail policy is applicable. */
  simulationType?: TSimulationType;
}

// ================================
// Internal Constants
// ================================

/**
 * Legacy-compatible fallback threshold used when `passed` is not provided
 * for pass-applicable simulation types.
 */
const PASSING_THRESHOLD = 0.7;

// ================================
// Public Domain Policy
// ================================

/**
 * Resolves the visual badge color for a score history entry.
 *
 * Decision flow:
 * 1. If pass/fail is applicable to the simulation type:
 *    - Prefer explicit `passed` verdict when present.
 *    - Otherwise compute pass/fail using score/maxScore threshold fallback.
 * 2. If pass/fail is not applicable:
 *    - Derive skill level from score.
 *    - Map skill level to a stable badge color token.
 * 3. If skill level cannot be resolved, return `yellow` as a parity-safe default.
 *
 * Behavior notes:
 * - We intentionally keep threshold fallback semantics here to match existing
 *   score-history behavior for sparse score payloads.
 * - `orange` is retained in the color union for compatibility with style
 *   variants, even though this policy currently maps awareness-level badges
 *   to `yellow`.
 */
export const resolveScoreHistoryBadgeColor = ({
  maxScore = APP_SCORING_MAX_SCORE,
  passed,
  runtimeRatingCutoffs,
  score,
  simulationType,
}: IScoreHistoryDisplayDecisionInput): TScoreHistoryBadgeColor => {
  // Branch A: Pass/fail-capable simulation types use pass-state color decisions.
  if (isPassApplicable(simulationType)) {
    // When upstream already computed pass/fail, trust that source directly.
    if (passed !== undefined) {
      // Use yellow background for "Developing" when V2 is enabled, red for "Fail" otherwise
      return passed ? 'pass' : FEATURE_FLAGS.ENABLE_SCORECARD_V2 ? 'yellow' : 'fail';
    }

    // Fallback when explicit pass state is absent: infer from threshold.
    const passedThreshold = score / maxScore >= PASSING_THRESHOLD;
    return passedThreshold ? 'pass' : FEATURE_FLAGS.ENABLE_SCORECARD_V2 ? 'yellow' : 'fail';
  }

  // Branch B: Non pass/fail simulation types use skill-level color mapping.
  const skillLevel = getSkillLevel(score, runtimeRatingCutoffs);

  // Color mapping preserves long-standing UI semantics.
  if (skillLevel === SKILL_LEVELS.ADVANCED) return 'blue';
  if (skillLevel === SKILL_LEVELS.AWARENESS) return 'yellow';
  if (skillLevel === SKILL_LEVELS.BEGINNER) return 'yellow';
  if (skillLevel === SKILL_LEVELS.EXPERT) return 'green';
  if (skillLevel === SKILL_LEVELS.INTERMEDIATE) return 'orange';
  if (skillLevel === SKILL_LEVELS.SKILLED) return 'purple';

  // Defensive fallback for null/unknown skill-level outcomes.
  return 'yellow';
};

/**
 * Resolves badge text content for a score history entry.
 *
 * Decision flow:
 * 1. Pass-applicable simulation types return canonical pass/fail labels.
 * 2. All other types return score text formatted via app score-format policy.
 *
 * Important:
 * - This function intentionally delegates numeric formatting to `formatScore`
 *   so that all score text formatting remains centralized and consistent.
 * - When ENABLE_SCORECARD_V2 is enabled, failed attempts display "Developing"
 *   instead of "Fail" to provide a more encouraging and growth-oriented message.
 */
export const formatScoreHistoryBadge = ({
  maxScore = APP_SCORING_MAX_SCORE,
  passed,
  score,
  simulationType,
}: IScoreHistoryDisplayDecisionInput): string => {
  console.log('simulationTypesimulationTypesimulationType', simulationType);
  // Certification/practice badges are semantic state labels, not numeric text.
  if (isPassApplicable(simulationType)) {
    if (passed && simulationType === WORDS.PRACTICE) {
      return SCORE_LABELS.READY_TO_CERTIFY;
    }
    if (passed) {
      return SCORE_LABELS.PASS;
    }
    // Use "Developing" instead of "Fail" when V2 feature flag is enabled
    return FEATURE_FLAGS.ENABLE_SCORECARD_V2 ? TAG_LABELS.DEVELOPING : SCORE_LABELS.FAIL;
  }

  // Non pass/fail contexts display formatted numeric/percentage score text.
  return formatScore(score, maxScore, APP_SCORING_FORMAT);
};

/**
 * Resolves skill level from the most recent score entry.
 */
export const resolveLatestScoreSkillLevel = (
  scores: ISimulationScore[],
): TScoreSkillLevel | null => {
  if (!scores.length) return null;

  const latest = scores[0];
  const scoreNum = selectSkillLevelInputValue({
    score: latest?.score,
    scorePercentage: latest?.scorePercentage,
  });

  return getSkillLevel(scoreNum);
};

/**
 * Resolves skill level derived from the highest score entry.
 *
 * Determine the highest skill level across all scores to display in the history list.
 * This iterates through all scores, extracts the relevant score value based on the app's
 * scoring format, and keeps track of the highest score to determine the corresponding skill
 * level. If there are no scores or all scores are invalid, it returns null.
 */
export const resolveHighestScoreSkillLevel = (
  scores: ISimulationScore[],
): TScoreSkillLevel | null => {
  if (!scores.length) return null;

  let highestScore = 0;

  scores.forEach((score) => {
    const scoreNum = selectSkillLevelInputValue({
      fallback: 0,
      score: score.score,
      scorePercentage: score.scorePercentage,
    });

    if (scoreNum > highestScore) {
      highestScore = scoreNum;
    }
  });

  return getSkillLevel(highestScore);
};
